var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { announce } from '../helper/announcer';
import { DENOMINATION_KEYS } from '../constants/nmpConstants';
var classOf = function (p) {
    if (p.type === 'numberPiece')
        return 'numberPiece';
    if (p.type === 'moneyPiece') {
        var mp = p;
        return mp.pieceType === 'coins' ? 'coin' : 'moneyBill';
    }
    return null;
};
var buildLabel = function (p, t) {
    var denomKey = DENOMINATION_KEYS[p.denomination];
    var denomName = denomKey ? t(denomKey) : p.denomination;
    if (p.type === 'moneyPiece') {
        var mp = p;
        return mp.pieceType === 'coins'
            ? t('text.moneyPieceCoinLabel', { denom: denomName })
            : t('text.moneyPieceLabel', { denom: denomName });
    }
    return t('text.numberPieceLabel', { denom: denomName });
};
// Rendered dimensions — accounts for the standalone-tens rotation path where
// `defaultDimensions.width > height` indicates a horizontal orientation.
var renderedDims = function (p) {
    var _a, _b, _c;
    var base = (_b = (_a = p.dimensions) !== null && _a !== void 0 ? _a : p.defaultDimensions) !== null && _b !== void 0 ? _b : { width: 55, height: 55 };
    var scale = (_c = p.scale) !== null && _c !== void 0 ? _c : 1;
    return { width: base.width * scale, height: base.height * scale };
};
var computeGroupBounds = function (members) {
    var minX = Infinity;
    var minY = Infinity;
    var maxX = -Infinity;
    var maxY = -Infinity;
    members.forEach(function (p) {
        var _a = renderedDims(p), width = _a.width, height = _a.height;
        if (p.center.x - width / 2 < minX)
            minX = p.center.x - width / 2;
        if (p.center.x + width / 2 > maxX)
            maxX = p.center.x + width / 2;
        if (p.center.y - height / 2 < minY)
            minY = p.center.y - height / 2;
        if (p.center.y + height / 2 > maxY)
            maxY = p.center.y + height / 2;
    });
    if (!Number.isFinite(minX))
        return null;
    var PAD = 6;
    return {
        left: minX - PAD,
        top: minY - PAD,
        width: maxX - minX + PAD * 2,
        height: maxY - minY + PAD * 2,
    };
};
var NumberAndMoneyPiecePicker = function () {
    var t = useTranslation().t;
    var _a = useContext(DataContext), manipulative = _a.manipulative, selectedTool = _a.selectedTool, setSelectedTool = _a.setSelectedTool;
    var _b = useState(null), sourceId = _b[0], setSourceId = _b[1];
    var containerRef = useRef(null);
    var firstBtnRef = useRef(null);
    var doneBtnRef = useRef(null);
    var returnFocusIdRef = useRef(null);
    // Keep the latest selectedTool accessible from event handlers (which live
    // on the dummy buttons and close over the initial render's value otherwise).
    var selectedRef = useRef(selectedTool);
    selectedRef.current = selectedTool;
    useEffect(function () {
        var onOpen = function (data) {
            setSourceId(data.sourceId);
            returnFocusIdRef.current = data.sourceId;
        };
        customEventHandler.addEventListener('openNmpPicker', onOpen);
        return function () { return customEventHandler.removeEventListener('openNmpPicker', onOpen); };
    }, []);
    // Resolve the source piece + class.
    var sourceItem = sourceId
        ? manipulative.find(function (m) { var _a; return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.id) === sourceId; })
        : undefined;
    var sourceData = sourceItem &&
        (sourceItem.data.type === 'numberPiece' || sourceItem.data.type === 'moneyPiece')
        ? sourceItem.data
        : null;
    var sourceClass = sourceData ? classOf(sourceData) : null;
    // Collect every same-class NMP piece, then split into standalones + groups.
    // A "group candidate" is any group whose members ALL match the source's
    // class. The picker treats the group as one toggleable unit so the user
    // can pull every member into the multi-selection in a single click.
    var candidates = useMemo(function () {
        if (!sourceClass || !sourceData)
            return [];
        var sameClassPieces = manipulative
            .filter(function (m) {
            if (m.data.type !== 'numberPiece' && m.data.type !== 'moneyPiece')
                return false;
            var d = m.data;
            return classOf(d) === sourceClass;
        })
            .map(function (m) { return m.data; });
        var groupMap = new Map();
        var standalonePieces = [];
        sameClassPieces.forEach(function (p) {
            var _a;
            if (p.groupId) {
                var arr = (_a = groupMap.get(p.groupId)) !== null && _a !== void 0 ? _a : [];
                arr.push(p);
                groupMap.set(p.groupId, arr);
            }
            else {
                standalonePieces.push(p);
            }
        });
        var out = [];
        // Groups first so their buttons sit lowest in DOM order — individual
        // peers (rendered after) stay above for direct interaction. The user
        // can still hit the group via the surrounding bounding-box padding.
        groupMap.forEach(function (members, groupId) {
            var bounds = computeGroupBounds(members);
            if (!bounds)
                return;
            out.push({ kind: 'group', groupId: groupId, members: members, bounds: bounds });
        });
        standalonePieces.forEach(function (piece) {
            out.push({ kind: 'standalone', piece: piece });
        });
        return out;
    }, [manipulative, sourceClass, sourceData]);
    // Seed selection on open: if the source is in a group, seed with the
    // entire group. Otherwise just the source piece.
    useEffect(function () {
        if (!sourceId || !sourceData)
            return;
        var seed = sourceData.groupId
            ? manipulative
                .filter(function (m) {
                if (m.data.type !== 'numberPiece' && m.data.type !== 'moneyPiece')
                    return false;
                return m.data.groupId === sourceData.groupId;
            })
                .map(function (m) { return m.data.id; })
            : [sourceId];
        if (selectedRef.current.length !== seed.length ||
            seed.some(function (s) { return !selectedRef.current.includes(s); })) {
            setSelectedTool(seed);
        }
        announce(t('text.nmpPickerOpened', { count: candidates.length }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceId]);
    // Auto-focus the first dummy button when the picker opens.
    useEffect(function () {
        if (!sourceId)
            return;
        requestAnimationFrame(function () { var _a; return (_a = firstBtnRef.current) === null || _a === void 0 ? void 0 : _a.focus(); });
    }, [sourceId]);
    // Self-tear-down when the source piece disappears or has no candidates left.
    useEffect(function () {
        if (!sourceId)
            return;
        if (!sourceData || candidates.length === 0)
            setSourceId(null);
    }, [sourceId, sourceData, candidates.length]);
    if (!sourceId || !sourceData || candidates.length === 0)
        return null;
    var restoreFocusToOrigin = function () {
        var id = returnFocusIdRef.current;
        if (!id)
            return;
        requestAnimationFrame(function () {
            var el = document.querySelector("[data-manipulative-id=\"".concat(id, "\"]"));
            el === null || el === void 0 ? void 0 : el.focus();
        });
    };
    var closePicker = function () {
        // Announce the final selection count so the user knows what they're
        // leaving the picker with. Uses the current ref so we don't close over
        // stale state.
        var allCandidateIds = new Set();
        candidates.forEach(function (c) {
            if (c.kind === 'standalone')
                allCandidateIds.add(c.piece.id);
            else
                c.members.forEach(function (m) { return allCandidateIds.add(m.id); });
        });
        var selectedCount = selectedRef.current.filter(function (sid) {
            return allCandidateIds.has(sid);
        }).length;
        announce(t('text.nmpPickerClosed', { count: selectedCount }));
        setSourceId(null);
        restoreFocusToOrigin();
    };
    var togglePiece = function (pieceId) {
        var current = selectedRef.current;
        if (current.includes(pieceId)) {
            setSelectedTool(current.filter(function (s) { return s !== pieceId; }));
        }
        else {
            setSelectedTool(__spreadArray(__spreadArray([], current, true), [pieceId], false));
        }
    };
    var toggleGroup = function (members) {
        var current = selectedRef.current;
        var memberIds = members.map(function (m) { return m.id; });
        var memberIdSet = new Set(memberIds);
        var anySelected = memberIds.some(function (id) { return current.includes(id); });
        if (anySelected) {
            // Mixed or fully-pressed → remove every group member from the
            // selection so the toggle's next click adds them back as a unit.
            setSelectedTool(current.filter(function (s) { return !memberIdSet.has(s); }));
        }
        else {
            var merged_1 = __spreadArray([], current, true);
            memberIds.forEach(function (id) {
                if (!merged_1.includes(id))
                    merged_1.push(id);
            });
            setSelectedTool(merged_1);
        }
    };
    var moveFocus = function (delta) {
        var _a;
        var root = containerRef.current;
        if (!root)
            return;
        var btns = Array.from(root.querySelectorAll('button.nmpPicker__target, button.nmpPicker__done'));
        if (btns.length === 0)
            return;
        var active = document.activeElement;
        var curIdx = active ? btns.indexOf(active) : -1;
        var nextIdx = curIdx === -1
            ? (delta > 0 ? 0 : btns.length - 1)
            : (curIdx + delta + btns.length) % btns.length;
        (_a = btns[nextIdx]) === null || _a === void 0 ? void 0 : _a.focus();
    };
    var onContainerKeyDown = function (e) {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            closePicker();
            return;
        }
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            e.stopPropagation();
            moveFocus(1);
        }
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            e.stopPropagation();
            moveFocus(-1);
        }
    };
    // Click-outside dismissal — gives iPad / no-Escape users a third exit
    // path alongside the visible Done button. Capture phase so we see the
    // tap before any underlying canvas listener consumes it.
    var onPointerDownCapture = function (e) {
        var target = e.target;
        var root = containerRef.current;
        if (!root)
            return;
        var inside = root.contains(target);
        if (!inside)
            closePicker();
    };
    var descriptionId = 'nmpPicker-description';
    var firstButtonAssigned = false;
    return (_jsxs("div", { ref: containerRef, className: "nmpPicker", role: "dialog", "aria-label": t('text.selectSimilarPieces'), "aria-describedby": descriptionId, "aria-modal": "false", onKeyDown: onContainerKeyDown, children: [_jsx("span", { id: descriptionId, style: {
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    margin: -1,
                    padding: 0,
                    overflow: 'hidden',
                    clip: 'rect(0,0,0,0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                }, children: t('text.nmpPickerDescription', { count: candidates.length }) }), _jsx("div", { className: "nmpPicker__backdrop", style: {
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'auto',
                    zIndex: 99998,
                }, onPointerDown: onPointerDownCapture }), candidates.map(function (cand) {
                if (cand.kind === 'group') {
                    var memberIds = cand.members.map(function (m) { return m.id; });
                    var memberIdSet_1 = new Set(memberIds);
                    var selectedMembers = selectedRef.current.filter(function (s) { return memberIdSet_1.has(s); });
                    var allSelected = selectedMembers.length === memberIds.length;
                    var partial = selectedMembers.length > 0 && !allSelected;
                    var ariaLabel = t('text.nmpPickerGroupTarget', {
                        count: cand.members.length,
                        defaultValue: 'Group of {{count}} pieces',
                    });
                    var isFirst_1 = !firstButtonAssigned;
                    if (isFirst_1)
                        firstButtonAssigned = true;
                    return (_jsx("button", { ref: isFirst_1 ? firstBtnRef : undefined, type: "button", className: "nmpPicker__target nmpPicker__target--group".concat(allSelected ? ' nmpPicker__target--selected' : '').concat(partial ? ' nmpPicker__target--partial' : ''), "data-target-id": cand.groupId, style: {
                            position: 'absolute',
                            left: cand.bounds.left,
                            top: cand.bounds.top,
                            width: cand.bounds.width,
                            height: cand.bounds.height,
                            zIndex: 99998,
                        }, onClick: function () { return toggleGroup(cand.members); }, "aria-label": ariaLabel, "aria-pressed": allSelected, "aria-checked": partial ? 'mixed' : allSelected, "aria-keyshortcuts": "Space Enter ArrowLeft ArrowRight ArrowUp ArrowDown Escape" }, cand.groupId));
                }
                var peer = cand.piece;
                var _a = renderedDims(peer), width = _a.width, height = _a.height;
                var left = peer.center.x - width / 2;
                var top = peer.center.y - height / 2;
                var label = buildLabel(peer, t);
                var isSelected = selectedTool.includes(peer.id);
                var isSource = peer.id === sourceId;
                var isFirst = !firstButtonAssigned;
                if (isFirst)
                    firstButtonAssigned = true;
                return (_jsx("button", { ref: isFirst ? firstBtnRef : undefined, type: "button", className: "nmpPicker__target".concat(isSelected ? ' nmpPicker__target--selected' : '').concat(isSource ? ' nmpPicker__target--source' : ''), "data-target-id": peer.id, style: {
                        position: 'absolute',
                        left: left,
                        top: top,
                        width: width,
                        height: height,
                        zIndex: 99999,
                    }, onClick: function () { return togglePiece(peer.id); }, "aria-label": label, "aria-pressed": isSelected, "aria-keyshortcuts": "Space Enter ArrowLeft ArrowRight ArrowUp ArrowDown Escape" }, peer.id));
            }), (function () {
                var _a = renderedDims(sourceData), srcW = _a.width, srcH = _a.height;
                var left = sourceData.center.x - srcW / 2;
                var top = sourceData.center.y - srcH / 2 - 48;
                return (_jsx("button", { ref: doneBtnRef, type: "button", className: "nmpPicker__done", style: {
                        position: 'absolute',
                        left: left,
                        top: top,
                        zIndex: 100000,
                    }, onClick: closePicker, "aria-label": t('text.nmpPickerDone', { defaultValue: 'Done selecting' }), children: t('text.done', { defaultValue: 'Done' }) }));
            })()] }));
};
export default NumberAndMoneyPiecePicker;
